import { call, fork, put, select } from 'redux-saga/effects';
import { getFileInfoById } from "../../file";
import { INTELLIGENT_PHOTO_CAPTURE_IMAGE_BATCH_PROCESSING_REQUEST, photoCaptureDataFailureAction, photoCaptureIntelligentAddEstimatedSummaryFileInfoAction, photoCaptureIntelligentDeleteAttachmentIterationAction, photoCaptureIntelligentUpdateIterationUploadFailedCountAction, photoCaptureIntelligentUpdateUploadedFilesCountAction } from '../actions';
import { IntelligentAssignmentSummary, SERVICE_FAIL_STATUS } from '../constants';
import { IntelligentPhotoCaptureImageBatchProcessorType } from '../enums';
import { getAttachmentIterationInfo, getIntelligentMaxConcurrentBatchAllowed, getIntelligentMaxPhotoLimitForBatch } from '../selectors';
import { attachmentIteration, createBatchProcessorChain, isFileUploadStatusFailed } from '../utilities';
import { intelligentPhotoCaptureBatchUploadSaga } from './intelligentPhotoCaptureBatchUploadSaga';
import { intelligentPhotoCaptureEstimatorImageAnalyzeSaga } from './intelligentPhotoCaptureEstimatorImageAnalyzeSaga';
import { intelligentPhotoCaptureFailureLoggingSaga } from './intelligentPhotoCaptureFailureLoggingSaga';
/**
 * Helper generator to get the image batch config for non-upload-files workflows.
 * Returns config with UPDATE_PATCH and IMAGE_ANALYZER processors.
 */
export function* getImageBatchConfig() {
    const batchSize = yield select(getIntelligentMaxPhotoLimitForBatch);
    const maxConcurrentBatches = yield select(getIntelligentMaxConcurrentBatchAllowed);
    return {
        batchSize,
        maxConcurrentBatches,
        processors: [
            {
                name: IntelligentPhotoCaptureImageBatchProcessorType.UPDATE_PATCH,
                fn: function* (batch, batchId) {
                    return yield call(intelligentPhotoCaptureBatchUploadSaga, batch, batchId);
                }
            },
            {
                name: IntelligentPhotoCaptureImageBatchProcessorType.IMAGE_ANALYZER,
                fn: function* (batch, batchId) {
                    return yield call(intelligentPhotoCaptureEstimatorImageAnalyzeSaga, batch, batchId);
                }
            }
        ]
    };
}
/**
 * Batch processor chain instance for handling file uploads
 */
export const batchChain = createBatchProcessorChain();
/**
 * Saga that processes uploaded images in batches
 * Triggered when a file upload completes
 * Only processes primary images (not thumbnails)
 *
 * @param action - The file upload complete action
 * @yields Effects for Redux-Saga
 */
export function* intelligentPhotoCaptureImageBatchProcessingSaga(action) {
    try {
        const { id } = action.payload;
        const fileInfo = yield select(getFileInfoById, id);
        if (!fileInfo) {
            throw new Error(IntelligentAssignmentSummary.FILE_NOT_FOUND);
        }
        const isPrimaryImage = !fileInfo.parentId;
        const currentInProgressIteration = attachmentIteration.getInProgressIteration(yield select(getAttachmentIterationInfo));
        const { iterationId, uploadedFilesCount = 0, uploadFailedCount = 0, iterationFiles } = currentInProgressIteration || {};
        if (isFileUploadStatusFailed(fileInfo) && isPrimaryImage) {
            if (iterationFiles && iterationFiles.length && iterationId !== undefined) {
                yield put(photoCaptureIntelligentUpdateIterationUploadFailedCountAction(iterationId, 1));
                if (iterationFiles.length === uploadFailedCount + IntelligentAssignmentSummary.UPLOADED_FILE_COUNT_INCREMENT) {
                    yield put(photoCaptureIntelligentDeleteAttachmentIterationAction(iterationId));
                    yield fork(intelligentPhotoCaptureFailureLoggingSaga, {
                        actionType: INTELLIGENT_PHOTO_CAPTURE_IMAGE_BATCH_PROCESSING_REQUEST,
                        errorDetail: SERVICE_FAIL_STATUS,
                        requestedPayload: fileInfo,
                        response: IntelligentAssignmentSummary.EDS_FILE_CREATION_ERROR
                    });
                }
            }
            return;
        }
        yield put(photoCaptureIntelligentAddEstimatedSummaryFileInfoAction(fileInfo));
        if (isPrimaryImage) {
            const file = yield select(getFileInfoById, fileInfo.id);
            if (!file) {
                throw new Error(IntelligentAssignmentSummary.FILE_NOT_FOUND);
            }
            const imageBatchConfig = yield call(getImageBatchConfig);
            yield call([batchChain, batchChain.addItemToProcessor], file, imageBatchConfig);
            // Flush any remaining files now that all uploads are complete
            if (iterationFiles && iterationFiles.length && iterationId !== undefined) {
                yield put(photoCaptureIntelligentUpdateUploadedFilesCountAction(iterationId));
                const fileCount = uploadedFilesCount + IntelligentAssignmentSummary.UPLOADED_FILE_COUNT_INCREMENT;
                if (iterationFiles.length - uploadFailedCount === fileCount && fileCount % imageBatchConfig.batchSize !== 0) {
                    yield call([batchChain, batchChain.flushRemainingBatch], imageBatchConfig);
                }
            }
        }
    }
    catch (error) {
        yield put(photoCaptureDataFailureAction(error.toString()));
    }
}
//# sourceMappingURL=intelligentPhotoCaptureImageBatchProcessingSaga.js.map